/**
 * Account-plane feed need (consumer feed filters, Stage C).
 *
 * Positions and resting orders are the one consumer of market rows that no
 * VIEW declares: `getMarketPrice` / `usePositionPnl` mark every held ticker
 * from the live store regardless of which page is open. So the account
 * stores themselves declare the need, TICKER-SCOPED (feed-filters-per-view,
 * 2026-09-12): the held / resting identities by name, with the class opt-in
 * their rows need — while any Kalshi position / open order, or any
 * Polymarket open order, may be a player prop, every default-excluded class
 * is opted back in under the `account-plane` source (fail open).
 *
 * Polymarket identities are opaque (token / conditionId) until the market has
 * been ingested once (the store's identity indexes are filled by ingest and
 * survive resets). An UNRESOLVED live Poly identity therefore fails OPEN: it
 * opts every default-excluded class back in until it resolves (Danielle R1
 * DNL-003 — with props excluded by default the market could otherwise never
 * arrive to classify itself: a deadlock closed around an active order).
 * Relay-held Poly POSITIONS and org-key resting orders reach this plane
 * through `polyRelayAccountStore`, mirrored on every route by
 * `AccountFeedNeedsBridge` (feed-filters-per-view, Danielle round 1) — the
 * former known gap ("a never-seen Poly prop position stays unclassified
 * until a prop surface ingests it") is closed: the position is named, the
 * classes fail open, the row arrives from the floor, the identity resolves.
 */
import { useAccountStore } from './accountStore';
import { usePolyAccountStore } from './polyAccountStore';
import { usePolyRelayAccountStore } from './polyRelayAccountStore';
import { useMarketStore } from './marketStore';
import { declareFeedNeeds } from './feedNeeds';
import { DEFAULT_EXCLUDED_CLASSES } from '@/workers/feedFilter';
import { resolveFeedTicker, tickerFeedNeeds } from '@/lib/feedFilter/tickerNeeds';
import { feedNeedsKey } from '@/hooks/useFeedNeeds';
import { sportFromTicker } from '@/lib/kalshiTicker/sportFilter';
import { normalizeSport } from './marketFrameNormalization';

export const ACCOUNT_PLANE_SOURCE = 'account-plane';

let _unsubscribe: (() => void) | null = null;
let _lastKey: string | null = null;
// Danielle R10 DNL-009: an UNRESOLVED Poly identity resolves when its market
// is ingested — a MARKET-store event, not an account-store one — so the
// needs must recompute on market-store ticks while anything is unresolved
// (throttled: the store ticks up to 30 Hz; the recompute is cheap but not
// free). Once everything resolves the market subscription goes quiet.
// Danielle R11 DNL-009: the throttle is TRAILING-EDGE — a tick that lands
// inside the window schedules ONE recompute for the window's end instead of
// being dropped. A leading-edge drop lost the causal update whenever an
// unrelated market consumed the slot and the resolving market arrived inside
// the window with nothing after it: the opt-in then stayed on indefinitely.
let _hasUnresolved = false;
let _lastMarketRecomputeMs = 0;
let _pendingMarketRecompute: ReturnType<typeof setTimeout> | null = null;
const MARKET_RECOMPUTE_THROTTLE_MS = 2_000;

function recomputeFromMarketTick(): void {
  const now = Date.now();
  const sinceLast = now - _lastMarketRecomputeMs;
  if (sinceLast >= MARKET_RECOMPUTE_THROTTLE_MS) {
    _lastMarketRecomputeMs = now;
    recomputeAccountFeedNeeds();
    return;
  }
  if (_pendingMarketRecompute != null) return; // one trailing recompute already booked
  _pendingMarketRecompute = setTimeout(() => {
    _pendingMarketRecompute = null;
    _lastMarketRecomputeMs = Date.now();
    recomputeAccountFeedNeeds();
  }, MARKET_RECOMPUTE_THROTTLE_MS - sinceLast);
}

/** One Polymarket identity (slug, conditionId or CLOB token id) → the slug
 *  the feed knows it by, or the UNRESOLVED sentinel while the store has not
 *  learned it. A `PM-` slug is returned verbatim even when unknown: the
 *  ticker parser can classify it, and the engines match it by name. */
function polyIdentityTicker(...ids: Array<string | null | undefined>): string {
  const market = useMarketStore.getState();
  let slug: string | null = null;
  for (const id of ids) {
    if (!id) continue;
    if (market.getMarket(id)) return id;
    slug = market.getTickerByConditionId(id) ?? market.getTickerByTokenId(id) ?? (id.startsWith('PM-') ? id : null);
    if (slug) return slug;
  }
  return UNRESOLVED_POLY_ORDER;
}

/** Pure: the set of account tickers to classify. Exported for tests.
 *  Sources: Kalshi positions + resting orders (accountStore), personal-key
 *  Poly resting orders (polyAccountStore), and — Danielle round 1 — the
 *  RELAY-held Poly positions and org-key resting orders mirrored by
 *  `AccountFeedNeedsBridge` into `polyRelayAccountStore`. */
export function accountTickers(): string[] {
  const acct = useAccountStore.getState();
  const poly = usePolyAccountStore.getState();
  const relay = usePolyRelayAccountStore.getState();
  const out = new Set<string>();
  for (const p of acct.positions) if (p.ticker) out.add(p.ticker);
  for (const o of acct.openOrders) if (o.ticker) out.add(o.ticker);
  for (const o of poly.openOrders) out.add(polyIdentityTicker(o.assetId, o.conditionId));
  for (const p of relay.positions) if (p.ticker) out.add(polyIdentityTicker(p.ticker));
  for (const o of relay.orders) {
    const asset = typeof o.asset_id === 'string' ? o.asset_id : null;
    const cond = typeof o.market === 'string' ? o.market : null;
    if (asset || cond) out.add(polyIdentityTicker(asset, cond));
  }
  return [...out];
}

/** Sentinel for a live Poly order whose identity has not been learned yet —
 *  classified as a POSSIBLE prop (fail open). */
export const UNRESOLVED_POLY_ORDER = 'PM-?unresolved';

/** Sports of the account's tickers — the frame's own sport when the row is
 *  in the store, else the ticker parse. Diagnostic only since the two-tier
 *  floor: the account no longer declares sports (see recomputeAccountFeedNeeds). */
export function accountSports(tickers: readonly string[]): Iterable<string> | 'all' {
  const market = useMarketStore.getState();
  const out = new Set<string>();
  for (const t of tickers) {
    if (t === UNRESOLVED_POLY_ORDER) return 'all';
    const s = normalizeSport(market.getMarket(t)?.sport ?? '') ?? sportFromTicker(t);
    if (s == null) return 'all';
    out.add(s);
  }
  return out;
}

export function recomputeAccountFeedNeeds(): void {
  const tickers = accountTickers();
  const unresolved = tickers.includes(UNRESOLVED_POLY_ORDER);
  const ids = tickers.filter((t) => t !== UNRESOLVED_POLY_ORDER);
  // Recompute on market ticks while any identity is unresolved OR any listed
  // row is not in the store yet — the fold (its class) is learned from the
  // row when it lands (from the floor, at worst), and an unresolved Poly
  // order resolves the same way (DNL-009).
  const market = useMarketStore.getState();
  _hasUnresolved = unresolved || ids.some((t) => market.getMarket(resolveFeedTicker(t)) == null);
  if (ids.length === 0 && !unresolved) {
    if (_lastKey !== '') { _lastKey = ''; declareFeedNeeds(ACCOUNT_PLANE_SOURCE, null); }
    return;
  }
  // The account plane is TICKER-SCOPED (feed-filters-per-view): it names its
  // held / resting identities, so on a page that names nothing else (the
  // Position Tracker, Settings) the edge carries exactly those rows at
  // browser cadence instead of the firehose it pulled before 2026-09-12.
  //
  // Fold 'floor': beside a broad sibling it contributes NO sport and NO game
  // of its own — a held position must not keep an edge hot for every sport
  // it sits in (that made every previously visited sport stay "full pipe",
  // Dan 2026-09-02); off-tab marks come from the hc1 floor (every sport at
  // 1 s), and an omitted axis would cancel a game detail's event narrowing.
  // Class opt-ins fail OPEN as before: a ticker outside the front-page
  // families may be ANY detail-only class, and an UNRESOLVED Poly order
  // could otherwise never arrive to classify itself (DNL-003).
  const needs = tickerFeedNeeds(ids, { fold: 'floor' });
  if (unresolved) needs.classes = [...DEFAULT_EXCLUDED_CLASSES];
  const key = feedNeedsKey(needs);
  if (key === _lastKey) return;
  _lastKey = key;
  declareFeedNeeds(ACCOUNT_PLANE_SOURCE, needs);
}

/** Subscribe once per fleet lifetime; idempotent. */
export function startAccountFeedNeeds(): void {
  if (_unsubscribe) return;
  const unsubs = [
    useAccountStore.subscribe(recomputeAccountFeedNeeds),
    usePolyAccountStore.subscribe(recomputeAccountFeedNeeds),
    usePolyRelayAccountStore.subscribe(recomputeAccountFeedNeeds),
    useMarketStore.subscribe((s, prev) => {
      if (s.version === prev.version || !_hasUnresolved) return;
      recomputeFromMarketTick();
    }),
  ];
  _unsubscribe = () => { for (const u of unsubs) u(); };
  recomputeAccountFeedNeeds();
}

export function stopAccountFeedNeeds(): void {
  _unsubscribe?.();
  _unsubscribe = null;
  _lastKey = null;
  _hasUnresolved = false;
  _lastMarketRecomputeMs = 0;
  if (_pendingMarketRecompute != null) { clearTimeout(_pendingMarketRecompute); _pendingMarketRecompute = null; }
  declareFeedNeeds(ACCOUNT_PLANE_SOURCE, null);
}
